import './styles.scss';

import { Col, Row } from 'react-bootstrap';
import { Datetime, createNotification } from '@/utils';
import DeferredFileUpload, { DeferredFileUploadHandle } from '@/components/FileUpload/deferred-file-upload';
import { DispatcherDropdownField, DriverDropdownField } from '@/components/UserDropdown';
import GenericFileUpload, { GenericFile, GenericFileType } from '@/components/FileUpload/new';
import { useMemo, useRef, useState } from 'react';

import { AssignVehicleDropdownField } from '@/components/VehicleDropdown';
import { CurrencyInputField } from '@/components/CurrencyInput';
import { DATETIME_FORMAT } from '@/constants';
import { DatetimeInputField } from '@/components/DatetimeInput';
import Field from '@/common/Field';
import { FlightNumberOrLoopNameInputField } from '@/common/FlightNumberInput';
import Form from '@/common/Form';
import { InputField } from '@/components/Input';
import { LoadingBlur } from '@/components/LoadingSpinner';
import LostAndFoundFormHistoryTable from '@/features/LostAndFoundHistory/components/LostAndFoundHistoryForm/LostAndFoundFormHistoryTable';
import { LostAndFoundHistoryFormDefinitions } from '@/features/LostAndFoundHistory/components/LostAndFoundHistoryForm/types';
import { PhoneInputField } from '@/components/MaskedInput/PhoneInput';
import QuerySelect from '@/common/QuerySelect';
import { ReturnStatusDropdownField } from '@/features/LostAndFoundHistory/components/LostAndFoundHistoryForm/ReturnStatusDropdown';
import { TextAreaField } from '@/components/TextArea';
import { Toast } from '@/models';
import { createLostAndFoundIssue } from '@/features/LostAndFoundHistory/api/services/createLostAndFoundIssue';
import { createLostAndFoundIssueDocumentPresignedUrl } from '../../api/services/createLostAndFoundIssueDocumentPresignedUrlEndpoint';
import { deleteLostAndFoundIssueDocument } from '../../api/services/deleteLostAndFoundIssueDocument';
import { getStatesOptions } from '@/common/QuerySelect/utils';
import { recreateLostAndFoundIssueDocumentDownloadUrl } from '../../api/services/createLostAndFoundIssueDocumentDownloadURL';
import { updateLostAndFoundIssue } from '@/features/LostAndFoundHistory/api/services/updateLostAndFoundIssue';
import useConfirmation from '@/hooks/useConfirmation';
import useLocale from '@/hooks/useLocale';
import { useLostAndFoundHistory } from '@/features/LostAndFoundHistory';
import { useServiceIssueFilters } from '@/hooks/useServiceIssueFilters';

const LostAndFoundHistoryForm = ({
  onSubmit,
  onEditLostAndFound,
  locale: customLocale,
  value = {},
  trip,
}: LostAndFoundHistoryFormDefinitions.Props): JSX.Element => {
  const [, setState, { submitFilters }] = useLostAndFoundHistory();
  const [documents, setDocuments] = useState(value.documents ?? []);
  const [loading, setLoading] = useState(false);
  const fileUploadRef = useRef<DeferredFileUploadHandle>(null);
  const locale = useLocale({
    ...LostAndFoundHistoryFormDefinitions.getLocale(value),
    ...customLocale,
  });
  const confirmDocumentDelete = useConfirmation();
  const handleDeleteDocument = (id: string) => {
    confirmDocumentDelete()
      .then(async () => {
        try {
          await deleteLostAndFoundIssueDocument([{ id }]);
          createNotification('Sucessfully Deleted Document', Toast.Type.SUCCESS, 'Lost And Found Issue');
          value.documents = value.documents.filter((document) => document.id !== id);
          setDocuments((current) => current.filter((document) => document.id !== id));
          await submitFilters?.(useServiceIssueFilters.getState().state); // Because we aren't resubmitting on closing the form
        } catch (e) {
          console.error('Error Deleting Document', e);
          createNotification('Could Not Delete Document', Toast.Type.DANGER, 'Lost And Found Issue');
        }
      })
      .catch(() => console.log('Cancelled document delete'));
  };
  const handleSubmit = async (data) => {
    try {
      setLoading(true);
      if (data.id) {
        const { tripAirportCode, ...update } = data;
        await updateLostAndFoundIssue([update]);
        createNotification('Sucessfully Updated Lost And Found', Toast.Type.SUCCESS, 'Lost And Found Issue');
        setState((current) => ({
          ...current,
          reports: current.reports.set(data.id, {
            ...current.reports.get(data.id),
            ...data,
          }),
        }));
      } else {
        const filesToUpload = fileUploadRef.current?.getFilesToUpload() ?? [];
        const { id, tripAirportCode, ...create } = data;
        const val = await createLostAndFoundIssue([
          { ...create, documents: filesToUpload.map((file) => ({ ...file, issueId: '', fileLocation: '' })) },
        ]);
        await fileUploadRef.current?.performDeferredUploads(val?.[0]?.output?.document ?? []); // No bulk create, so we only get back one issue
        createNotification('Sucessfully Created Lost And Found', Toast.Type.SUCCESS, 'Lost And Found Issue');
      }
    } catch (err) {
      console.error('Error Creating Lost and Found.', err);
      createNotification('Could Not Create Lost And Found', Toast.Type.DANGER, 'Lost And Found Issue');
    } finally {
      await submitFilters?.(useServiceIssueFilters.getState().state);
      setLoading(false);
      onSubmit?.(data);
    }
  };
  const fullValue = useMemo(
    () => ({
      ...LostAndFoundHistoryFormDefinitions.initValue,
      ...value,
      ...LostAndFoundHistoryFormDefinitions.loadForm(),
    }),
    []
  );

  return (
    <Form
      name="LostAndFoundHistoryForm"
      className="LostAndFoundHistoryForm"
      onSubmit={handleSubmit}
      value={fullValue}
      validator={LostAndFoundHistoryFormDefinitions.validator(locale)}
      onChange={LostAndFoundHistoryFormDefinitions.saveForm}
    >
      <LoadingBlur loading={loading} />
      <Form.Input name="id" type="hidden" />
      <Form.Input name="tripId" type="hidden" />
      <Form.Input name="tripAirportCode" type="hidden" />
      <Row className="LostAndFoundHistoryFormHeader">
        <Col>
          {value.tripId && (
            <>
              <span>
                {value.tripServicerIataAirlineCode}
                {value.tripLoopName || value.tripFlightNumber.toString().padStart(4, '0')}{' '}
                {new Datetime(value.tripScheduled).format(DATETIME_FORMAT)}
              </span>
              <span>
                {value.tripAirportCode} - {[value.driverFirstName, value.driverLastName].join(' ')} - #{value.vehicleTrackingId}
              </span>
            </>
          )}
          <h4 className="LostAndFoundHistoryFormTitle">{locale('Lost and Found')}</h4>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Input as={TextAreaField} name="description" label={locale('Description of Item')} validate />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Watch name="dispatcherId">
            {([dispatcherId]: string[]) => (
              <Form.Input
                as={DispatcherDropdownField}
                name="dispatcherId"
                label={locale('Dispatcher')}
                validate={dispatcherId !== null || value.tripId !== null}
              />
            )}
          </Form.Watch>
        </Col>
        <Col xs={6}>
          <Form.Watch name="itemLost">
            {([itemLost]: string[]) => (
              <Form.Input
                as={InputField}
                name="itemLost"
                label={locale('Item Lost')}
                validate={itemLost !== null || value.tripId !== null}
              />
            )}
          </Form.Watch>
        </Col>
      </Row>
      <Form.Watch name={['tripAirportCode', 'crewAirportCode', 'driverId', 'vehicleId']}>
        {([tripAirportCode, crewAirportCode, driverId, vehicleId]: string[]) => {
          return (
            <Row>
              <Col xs={6}>
                <Form.Input
                  as={DriverDropdownField}
                  airports={crewAirportCode ?? tripAirportCode}
                  name="driverId"
                  label={locale('Driver Name')}
                  validate={driverId !== null || value.tripId !== null}
                />
              </Col>
              <Col xs={6}>
                <Form.Input
                  as={AssignVehicleDropdownField}
                  name="vehicleId"
                  label={locale('Van Number')}
                  validate={vehicleId !== null || value.tripId !== null}
                  airportCode={crewAirportCode ?? tripAirportCode}
                />
              </Col>
            </Row>
          );
        }}
      </Form.Watch>
      <Row>
        <Col xs={6}>
          <Form.Input as={FlightNumberOrLoopNameInputField} name="outboundFlightName" label={locale('Outbound FLT #')} />
        </Col>
        <Col xs={6}>
          <Form.Input as={DatetimeInputField} name="outboundDatetime" label={locale('Outbound Date/Time')} />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input as={InputField} name="crewMemberName" label={locale('Crew Member Name')} validate />
        </Col>
        <Col xs={6}>
          <Form.Input as={PhoneInputField} name="crewMemberPhone" label={locale('Crew Member Phone')} validate />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input as={InputField} name="shippingAddress" label={locale('Shipping Address')} />
        </Col>
        <Col xs={6}>
          <Form.Watch name="shippingStateCode">
            {([stateCode]: string[], form) => (
              <Field label="State:">
                <QuerySelect
                  value={stateCode}
                  onChange={(val) => {
                    form.setValue('shippingStateCode', val);
                    form.trigger();
                  }}
                  query={getStatesOptions}
                  placeholder="State"
                />
              </Field>
            )}
          </Form.Watch>
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input as={InputField} name="shippingCity" label={locale('City')} />
        </Col>
        <Form.Watch name="shippingZipCode">
          {([zipCode]: string) => (
            <Col xs={6}>
              <Form.Input
                as={InputField}
                name="shippingZipCode"
                label={locale('Zip Code')}
                value={zipCode}
                validate={zipCode?.length > 0}
              />
            </Col>
          )}
        </Form.Watch>
      </Row>
      <Row>
        <Col>
          <Form.Watch name="crewMemberEmail">
            {([email]: string) => (
              <Form.Input
                as={InputField}
                type="email"
                name="crewMemberEmail"
                label={locale('Crew Member Email')}
                validate={email?.length > 0}
              />
            )}
          </Form.Watch>
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input as={ReturnStatusDropdownField} name="returnStatus" label={locale('Return Status')} validate />
        </Col>
        <Col xs={6}>
          <Form.Input as={InputField} name="trackingNumber" label={locale('Tracking Number')} />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input as={InputField} name="shippingMethod" label={locale('Shipping Method')} />
        </Col>
        <Col xs={6}>
          <Form.Input as={CurrencyInputField} name="shippingCost" label={locale('Shipping Cost')} />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Input as={TextAreaField} name="notes" label={locale('Notes')} />
        </Col>
      </Row>
      <Form.Watch name={['crewAirportCode', 'crewFlightNumber', 'crewDriverName']}>
        {(crewVal: string[]) => {
          if (!value.id || crewVal.filter((val) => val !== null).length === 0) return null;
          const [crewAirportCode, crewFlightNumber, crewDriverName] = crewVal;
          const CrewAirportInput =
            crewAirportCode !== null ? (
              <Form.Input as={InputField} name="crewAirportCode" label={locale('Airport')} disabled validate value={crewAirportCode} />
            ) : null;
          const CrewFlightInput =
            crewFlightNumber !== null ? (
              <Form.Input
                as={FlightNumberOrLoopNameInputField}
                name="crewFlightNumber"
                label={locale('Flight #')}
                disabled
                validate
                value={crewAirportCode}
              />
            ) : null;
          const CrewDriverInput =
            crewDriverName !== null ? (
              <Form.Input as={InputField} name="crewDriverName" label={locale('Driver Name')} disabled validate value={crewDriverName} />
            ) : null;
          return (
            <>
              <Row className="LostAndFoundHistoryFormSectionHeader">
                <Col>
                  <h4 className="LostAndFoundHistoryFormCommentsTitle">{locale('Additional Crew Supplied Information')}</h4>
                </Col>
              </Row>
              <Row>
                <Col>{CrewAirportInput}</Col>
              </Row>
              <Row>
                <Col>{CrewFlightInput}</Col>
              </Row>
              <Row>
                <Col>{CrewDriverInput}</Col>
              </Row>
            </>
          );
        }}
      </Form.Watch>
      <Row>
        <Col>
          <Form.Button name="SUBMIT" type="submit" variant={locale('SUBMIT_BUTTON_COLOR')}>
            {locale('SUBMIT_BUTTON_TEXT')}
          </Form.Button>
        </Col>
      </Row>
      {value.id && (
        <>
          <Row className="LostAndFoundHistoryFormUpload">
            <Col>
              <GenericFileUpload
                endpoint={async (file: GenericFileType) => {
                  try {
                    const payload = {
                      filename: file.file.name,
                      md5: file.md5,
                      fileSize: file.file.size,
                      issueId: value.id,
                    };
                    const res = await createLostAndFoundIssueDocumentPresignedUrl(payload);
                    createNotification('Sucessfully Uploaded Document', Toast.Type.SUCCESS, 'Lost And Found Issue');
                    return [res?.url, res?.contentType];
                  } catch (e) {
                    console.error('Error Uploading Document', e);
                    createNotification('Could Not Upload Document', Toast.Type.DANGER, 'Lost And Found Issue');
                  }
                }}
                options={{
                  showFiles: true,
                  multiple: true,
                }}
                locale={{
                  Upload: 'UPLOAD FILES',
                }}
                onError={async (...args) => console.log('', ...args)}
                onSuccess={async (...args) => {
                  await submitFilters?.(useServiceIssueFilters.getState().state);
                  const documents = await recreateLostAndFoundIssueDocumentDownloadUrl({ issueId: [value.id] });
                  const res = documents.urls.map((document) => {
                    const md5Match = document.url.match(/lost_and_found\/([a-f0-9]{32})\//); // Extract md5 from URL
                    const md5 = md5Match ? md5Match[1] : null;
                    return {
                      md5,
                      url: document.url,
                    };
                  });
                  return res;
                }}
              />
            </Col>
          </Row>
          {documents?.map((document) => (
            <Row className="mb-2" key={document.id}>
              <Col>
                <GenericFile name={document.filename} preview={document.fileLocation} onDelete={() => handleDeleteDocument(document.id)} />
              </Col>
            </Row>
          ))}
        </>
      )}
      {!value.id && (
        <>
          <Row className="LostAndFoundHistoryFormUpload">
            <Col>
              <DeferredFileUpload ref={fileUploadRef} options={{ multiple: true, showFiles: true }} />
            </Col>
          </Row>
        </>
      )}
      {
        /* Outbound Flight fields appears if trip linked */
        value.tripId && (
          <>
            <Row className="LostAndFoundHistoryFormHistory">
              <Col>
                <h4 className="LostAndFoundHistoryFormHistoryTitle">{locale('Lost and Found History')}</h4>
              </Col>
            </Row>
            <Row>
              <Col>
                <LostAndFoundFormHistoryTable
                  onEditLostAndFound={onEditLostAndFound}
                  id={value.tripId}
                  driverFirstName={trip?.driver?.firstName}
                  driverLastName={trip?.driver?.lastName}
                  vehicleTrackingId={trip?.vehicle?.trackingId}
                />
              </Col>
            </Row>
          </>
        )
      }
    </Form>
  );
};

export default LostAndFoundHistoryForm;
